/**
 * V6-010 砺锋台 V2 — 类型定义
 *
 * @see design/v6-sharpen/gdd-v6-010-sharpen.md §11 §21.6
 * @date 2026-07-19
 * @updated 2026-07-20 — 提案 1/2 数据模型追加
 */

/** 作战类型（4 种，large 暂未开放） */
export type BattleType = "1v1" | "2v2" | "3v3" | "large";

/** 已开放类型（不含 large） */
export type OpenType = "1v1" | "2v2" | "3v3";

/** 已开放类型列表（用于遍历） */
export const OPEN_TYPES: OpenType[] = ["1v1", "2v2", "3v3"];

/** 4 段军衔 ID（与 climb-manifest.phases 对齐） */
export type RankId = "soldier" | "officer" | "field_officer" | "general";

/** 军衔配置 */
export interface RankConfig {
  id: RankId;
  name: string;       // "战士" / "军官" / "校官" / "将帅"
  displayName: string; // "★ 战士" / "★★ 军官" / "★★★ 校官" / "★★★★ 将帅"
  index: number;       // 0-3
  aiLevelRange: [number, number]; // [min, max]
  floorCount: number;  // 该段关卡数
}

/** 4 段军衔配置表 */
export const RANK_CONFIGS: RankConfig[] = [
  { id: "soldier",       name: "战士", displayName: "★ 战士",     index: 0, aiLevelRange: [1, 3],  floorCount: 3 },
  { id: "officer",       name: "军官", displayName: "★★ 军官",    index: 1, aiLevelRange: [4, 6],  floorCount: 3 },
  { id: "field_officer", name: "校官", displayName: "★★★ 校官",  index: 2, aiLevelRange: [7, 9],  floorCount: 3 },
  { id: "general",       name: "将帅", displayName: "★★★★ 将帅", index: 3, aiLevelRange: [10, 10], floorCount: 1 },
];

/** aiLevel → RankId 映射 */
export function getRankForAiLevel(aiLevel: number): RankId {
  if (aiLevel <= 3) return "soldier";
  if (aiLevel <= 6) return "officer";
  if (aiLevel <= 9) return "field_officer";
  return "general";
}

/** 段位推进阈值（本段通关率 ≥ 此值解锁下一段） */
export const RANK_PROMOTION_THRESHOLD = 0.8;

/** Mock 关卡定义 */
export interface MockLevel {
  id: string;            // "c001" / "c001-2v2" / "c001-3v3"
  name: string;          // "初战" / "初战·双坦"
  aiType: string;        // "passive" / "aggressive" 等
  aiLevel: number;       // 1-10
  description: string;
  rank: RankId;          // 所属军衔段（提案 1）
}

/** 个人最佳记录（每关） */
export interface PersonalBest {
  bestHpPct: number;       // 最高 HP%（含输）
  bestTimeMs: number;      // 最短用时（仅赢时刷新）
  longestStreak: number;   // 通关此关时最长连胜
  firstClearAt: number;    // 首次通关时间戳
  clearCount: number;      // 通关次数
}

/** 破纪录字段类型 */
export type RecordField = "hp" | "time" | "streak";

/** 单场战斗记录 */
export interface BattleRecord {
  levelId: string;
  levelName: string;
  aiType: string;
  result: "win" | "loss";
  hpPct: number;         // 玩家 HP 剩余百分比
  timeMs: number;        // 战斗用时（毫秒）
  exp: number;           // 获得经验（1 或 0）
  timestamp: number;
  rank: RankId;          // 本关所属军衔段
  isNewRecord: boolean;  // 是否打破任一 PB
  recordBroken: RecordField[]; // 本场破的 PB 字段
  isRankUp: boolean;     // 是否触发段位晋升
}

/** 段位进度 */
export interface RankProgressEntry {
  cleared: number;       // 该段已通关数
  total: number;         // 该段总关卡数
  battles: number;       // 该段总场次
}

/** 每类型的玩家进度 */
export interface TypeProgress {
  exp: number;                  // 总经验（level 由 exp 推导）
  totalBattles: number;
  wins: number;
  losses: number;
  clearedLevels: string[];      // 已通关关卡 ID（赢过的）
  failedLevels: string[];       // 挑战过但未通关的关卡 ID（V6-012 四样式区分）
  recentBattles: BattleRecord[]; // 最近 3 场
  code: string;                 // 玩家代码（每类型独立）
  currentRank: RankId;          // 玩家当前所在段位
  rankProgress: Record<RankId, RankProgressEntry>; // 各段位进度
  pbMap: Record<string, PersonalBest>; // key = levelId，个人最佳
  personalBests: {
    longestStreak: number;      // 当前类型最长连胜
    currentStreak: number;      // 当前连胜
  };
}

/** 全类型汇总 */
export interface SharpenAggregate {
  totalBattles: number;
  totalWins: number;
  totalExp: number;
  longestStreakAllTypes: number;
  avgWinRate: number;
}

/** 砺锋台总状态 */
export interface SharpenState {
  typeProgress: Record<OpenType, TypeProgress>;
  lastSelectedType: OpenType;
  aggregate: SharpenAggregate;
  /** V6-011 数据版本（mock→真实关卡迁移，v1→v2 时自动清空） */
  _version: number;
}

// ── V6-011 真实关卡数据驱动 ──

/** V6-011 真实关卡条目（替代 MockLevel） */
export interface LevelEntry {
  id: string;              // levelId, e.g. "v5_0-1_first_contact"
  name: string;            // 显示名
  difficulty: string;      // "tutorial" | "easy" | "normal" | "hard" | "expert"
  difficultyStars: number; // 1-5
  mapSize: number;         // 棋盘尺寸
  maxUnitsPerTeam: number; // 1|2|3
  rank: RankId;            // 从 difficulty 推导
  type: OpenType;          // "1v1"|"2v2"|"3v3"
}

/** V6-011 棋盘分页 */
export interface BoardPage {
  pageIdx: number;       // 0-based
  totalPages: number;
  pieces: import('./tactical-map').BoardPiece[];
}

/**
 * 每页关卡数
 * V6-011: 65 实现 85% 关卡 + 15% 地形（65+12=77 填满整页）
 */
export const LEVELS_PER_PAGE = 65;

/** difficulty → aiLevel（模拟战斗用） */
export function difficultyToAiLevel(d: string): number {
  switch (d) {
    case "tutorial": return 1;
    case "easy": return 3;
    case "normal": return 5;
    case "hard": return 7;
    case "expert": return 10;
    default: return 3;
  }
}

/** difficulty → RankId */
export function difficultyToRank(d: string): RankId {
  switch (d) {
    case "tutorial": return "soldier";
    case "easy": return "soldier";
    case "normal": return "officer";
    case "hard": return "field_officer";
    case "expert": return "general";
    default: return "soldier";
  }
}

/** localStorage 键 */
export const STORAGE_KEY = "battlecode-sharpen-v3";

/** 类型标签 */
export const TYPE_LABELS: Record<BattleType, string> = {
  "1v1": "1v1 单挑",
  "2v2": "2v2 双坦",
  "3v3": "3v3 三坦",
  large: "大军团",
};

/** 类型描述 */
export const TYPE_DESCRIPTIONS: Record<BattleType, string> = {
  "1v1": "单坦克对决，基础战术训练",
  "2v2": "双坦克协同，独立决策",
  "3v3": "三坦克通信协同，高级战术",
  large: "暂未开放，敬请期待",
};

/** 类型主题色（CSS 变量值）— 2026-07-20 三类型用青系深浅 */
export const TYPE_COLORS: Record<BattleType, string> = {
  "1v1": "#4ECDC4",   // 战术青
  "2v2": "#7FE5DD",   // 青浅
  "3v3": "#5B9BD5",   // 钢蓝
  large: "#3A4555",   // 暗灰
};

/** 默认玩家代码模板（每类型相同，玩家可编辑） */
export const DEFAULT_CODE_TEMPLATE = `# === Bot 能力说明 ===
# 战术：基础进攻
# 使用 API：self.position, self.enemy, self.scan, self.shoot, self.move
# 核心策略：扫描-追击-开火循环
# === /Bot 能力说明 ===

def think(self):
    # 每帧扫描刷新视野
    self.scan()

    # 敌人可见时开火
    if self.enemy.is_visible:
        if self.cooldown_left == 0:
            # 冷却完毕，开火
            self.shoot()
        else:
            # 冷却中，向敌人移动
            self.move(self.enemy.position)
    else:
        # 敌人不可见，向其最后已知位置移动
        if self.enemy.last_known_position:
            self.move(self.enemy.last_known_position)
`;
